// Performance monitoring and Web Vitals tracking
// Provides insights into app performance for optimization

import React from 'react';
import { getCLS, getFID, getFCP, getLCP, getTTFB, Metric } from 'web-vitals';

// Performance metrics interface
interface PerformanceMetrics {
  cls: number | null;  // Cumulative Layout Shift
  fid: number | null;  // First Input Delay
  fcp: number | null;  // First Contentful Paint
  lcp: number | null;  // Largest Contentful Paint
  ttfb: number | null; // Time to First Byte
}

// Performance monitoring class
export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: PerformanceMetrics = {
    cls: null,
    fid: null,
    fcp: null,
    lcp: null,
    ttfb: null
  };
  
  private constructor() {
    this.initializeWebVitals();
    this.initializeCustomMetrics();
  }
  
  public static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }
  
  private initializeWebVitals() {
    // Track Core Web Vitals
    getCLS(this.handleMetric.bind(this, 'cls'));
    getFID(this.handleMetric.bind(this, 'fid'));
    getFCP(this.handleMetric.bind(this, 'fcp'));
    getLCP(this.handleMetric.bind(this, 'lcp'));
    getTTFB(this.handleMetric.bind(this, 'ttfb'));
  }
  
  private handleMetric(metricName: keyof PerformanceMetrics, metric: Metric) {
    this.metrics[metricName] = metric.value;
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`${metricName.toUpperCase()}: ${metric.value}`);
    }
    
    // Send to analytics in production
    if (process.env.NODE_ENV === 'production') {
      this.sendMetricToAnalytics(metricName, metric.value);
    }
  }
  
  private initializeCustomMetrics() {
    // Track API response times
    this.trackApiPerformance();
  }
  
  private trackApiPerformance() {
    // Intercept fetch calls to track API performance
    const originalFetch = window.fetch;
    
    window.fetch = async (...args) => {
      const startTime = performance.now();
      const url = args[0] as string;
      
      try {
        const response = await originalFetch(...args);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        this.logApiMetric(url, duration, response.status, 'success');
        
        return response;
      } catch (error) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        this.logApiMetric(url, duration, 0, 'error');
        throw error;
      }
    };
  }
  
  private logApiMetric(url: string, duration: number, status: number, type: 'success' | 'error') {
    const metric = {
      type: 'api-performance',
      url,
      duration,
      status,
      result: type,
      timestamp: Date.now()
    };
    
    if (process.env.NODE_ENV === 'development') {
      console.log('API Performance:', metric);
    }
    
    // Store for batch sending
    this.storeMetric(metric);
  }
  
  private storeMetric(metric: any) {
    // Store metrics in localStorage for batch sending
    try {
      const stored = localStorage.getItem('performance-metrics') || '[]';
      const metrics = JSON.parse(stored);
      metrics.push(metric);
      
      // Keep only last 100 metrics
      if (metrics.length > 100) {
        metrics.splice(0, metrics.length - 100);
      }
      
      localStorage.setItem('performance-metrics', JSON.stringify(metrics));
    } catch (error) {
      console.warn('Failed to store performance metric:', error);
    }
  }
  
  private sendMetricToAnalytics(metricName: string, value: number) {
    // Send to your analytics service
    // This is a placeholder - replace with your actual analytics implementation
    if (typeof (window as any).gtag !== 'undefined') {
      (window as any).gtag('event', 'web_vitals', {
        event_category: 'Performance',
        event_label: metricName,
        value: Math.round(value),
        non_interaction: true
      });
    }
  }
  
  public getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }
  
  public getStoredMetrics(): any[] {
    try {
      const stored = localStorage.getItem('performance-metrics') || '[]';
      return JSON.parse(stored);
    } catch {
      return [];
    }
  }
  
  public clearStoredMetrics(): void {
    localStorage.removeItem('performance-metrics');
  }
  
  // Manual performance tracking for specific operations
  public trackOperation<T>(name: string, operation: () => T): T {
    const startTime = performance.now();
    
    try {
      const result = operation();
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      this.logCustomMetric(name, duration, 'success');
      
      return result;
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      this.logCustomMetric(name, duration, 'error');
      throw error;
    }
  }
  
  public async trackAsyncOperation<T>(name: string, operation: () => Promise<T>): Promise<T> {
    const startTime = performance.now();
    
    try {
      const result = await operation();
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      this.logCustomMetric(name, duration, 'success');
      
      return result;
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      this.logCustomMetric(name, duration, 'error');
      throw error;
    }
  }
  
  private logCustomMetric(name: string, duration: number, result: 'success' | 'error') {
    const metric = {
      type: 'custom-operation',
      name,
      duration,
      result,
      timestamp: Date.now()
    };
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Custom Operation:', metric);
    }
    
    this.storeMetric(metric);
  }
}

// React hook for performance monitoring
export function usePerformanceMonitor() {
  const monitor = PerformanceMonitor.getInstance();
  
  const trackOperation = React.useCallback(
    <T>(name: string, operation: () => T): T => {
      return monitor.trackOperation(name, operation);
    },
    [monitor]
  );
  
  const trackAsyncOperation = React.useCallback(
    <T>(name: string, operation: () => Promise<T>): Promise<T> => {
      return monitor.trackAsyncOperation(name, operation);
    },
    [monitor]
  );
  
  const getMetrics = React.useCallback(() => {
    return monitor.getMetrics();
  }, [monitor]);
  
  return {
    trackOperation,
    trackAsyncOperation,
    getMetrics
  };
}

// Initialize performance monitoring
export function initializePerformanceMonitoring() {
  PerformanceMonitor.getInstance();
}